// Core entity types
export interface CommonAthleteStatistics {
    totalTrainingTime: number;
    level: string;
    strength?: number;
    strengthDate?: Date;
    endurance: number;
    resistance: number;
}

export interface CommonDisplaySettings {
    gradingSystem: GradingSystem;
    weightUnit?: WeightUnit;
    lang?: Lang;
}

export interface CommonPrivacySettings {
    privacy: Privacy;
}

export interface CommonUser {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
    weight: number;
    role: CommonRole;
    imageUrl: string;
    managingCommunities?: CommonCommunity[];
    displaySettings: CommonDisplaySettings;
    privacySettings: CommonPrivacySettings;
}

export interface CommonCommunity {
    id: number;
    name: string;
    gymName: string;
    description: string;
    address: CommonAddress;
    bannerImageUrl: string;
    iconImageUrl: string;
}

export interface CommonPost {
    id: number;
    comments: CommonComment[];
    usersWhoLiked: CommonUser[];
    author: CommonUser;
    message: string;
    date: Date;
}

export interface CommonComment {
    id: number;
    author: CommonUser;
    usersWhoLiked: CommonUser[];
    message: string;
    date: Date;
}

export type AcquisitionType = "test" | "training" | "raw";
export type AcquisitionSubType = null | "strength" | "endurance" | "resistance";

export interface CommonAcquisition {
    id: number;
    type: string;
    subtype: string;
    date: Date | string;
    title: string;
    tag: string;
    data: string;
    result: CommonAcquisitionResult;
}

export interface CommonAcquisitionResult {
    id: number;
    intervals?: CommonInterval[];
    intervalCount?: number;
    totalStrain?: number;
    avgIntervalDuration?: number;
    maxStrength?: number;
    durationVariation?: number;
    strengthVariation?: number;
}

export interface CommonInterval {
    id: number;
    start: number;
    end: number;
    maxStrength: number;
    stdDeviation: number;
    strain: number;
    strengthGeneration: number;
}

export interface CommonRole {
    isAdmin: boolean;
    isCoach: boolean;
}

export interface HeatmapData {
    count: number;
    date: string;
}

export interface StrengthGraphData {
    data: { x: Date, y: number }[];
}

export enum GymStatus {
    INACTIVE,
    ACTIVE,
    CLOSING,
    DELETED,
}

export namespace GymStatus {
    export function toString(status: GymStatus): string {
        switch (status) {
            case GymStatus.DELETED:
                return "Deleted";
            case GymStatus.ACTIVE:
                return "Active";
            case GymStatus.CLOSING:
                return "Closing";
            case GymStatus.INACTIVE:
            default:
                return "Inactive";
        }
    }
}

export interface CommonGym {
    id: number;
    name: string;
    phone: string;
    address: CommonAddress;
    owner: CommonUser;
    icon: string;
    systemCount: number;
    status: GymStatus;
    createdDate: Date;
}

export interface CommonAddress {
    id: number;
    line: string;
    city: string;
    province: string;
    zip: string;
    country: string;
}

export namespace CommonAddress {
    export function toString(address: CommonAddress): string {
        return `${address.line}, ${address.city}, ${address.province}, ${address.country}`;
    }
}

export enum OrderStatus {
    PENDING,
    SHIPPING,
    DONE,
    REJECTED
}

export namespace OrderStatus {
    export function toString(status: OrderStatus): string {
        switch (status) {
            case OrderStatus.DONE:
                return "Done";
            case OrderStatus.REJECTED:
                return "Rejected";
            case OrderStatus.SHIPPING:
                return "Shipping";
            case OrderStatus.PENDING:
            default:
                return "Pending";
        }
    }
}

export interface CommonOrder {
    id: number;
    gym: CommonGym;
    count: number;
    status: OrderStatus;
    createdDate: Date;
}

export type GradingSystem = "vermin" | "yosemite";
export type Privacy = "private" | "public";
export type RoleEnum = 'admin' | 'coach';

// API types
export interface GetActivitiesResponse {
    activities: Array<CommonAcquisition>;
    total: number;
}

export interface GetActivityQuery {
    title?: string;
    page?: number;
    type?: string;
    activitiesPerPage?: number;
}

export interface ObtainProfileInfoResponse {
    heatmapData: HeatmapData;
    athleteStatistics: CommonAthleteStatistics;
    polarChartData: any;
}

export interface ArchiveActivityBody {
    isArchived: boolean;
}

export interface UserSearchResult {
    key: number;
    id: number;
    title: string;
    image: string;
}

export interface CreateOrderBody { 
    gymId: number;
    count: number;
}

export interface GetOrdersQuery {
    status?: OrderStatus[];
    limit?: number;
}

export interface GetAdminListResponse {
    admins: Array<CommonUser>;
    total: number;
}

// Frontend-specific types
export interface CommonWorkout {
    id: number;
    name: string;
    title?: string; // For compatibility with components that expect title
    subtitle?: string; // For compatibility with components that expect subtitle
    description?: string;
    acquisitions: CommonAcquisition[];
    createdDate: Date;
}

export interface ActivityFiltersState {
    type?: string;
    subtype?: string;
    dateFrom?: Date;
    dateTo?: Date;
    title?: string;
}

export interface FetchUserResponse {
    user: CommonUser;
}

export type WeightUnit = "kg" | "lbs";
export type Lang = "en" | "es" | "fr" | "de";

// Utility types
export type Diff<T extends string, U extends string> = ({ [P in T]: P } & { [P in U]: never } & { [x: string]: never })[T];
export type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;